import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { from } from '@apollo/client/link';
import { ErrorLink } from '@apollo/client/link/error';
import { enterAfterDeniedRead } from './platform/routing';

// THE ONE PLACE THIS APP HEARS THE SERVER SAY NO. What to DO about it is not decided here — that is
// `enterAfterDeniedRead` in `@/lib/platform/routing`, which owns the whole rule and says why. All
// this link does is the translation Apollo's error shape needs: a GraphQL `errors` array arrives
// wrapped in `CombinedGraphQLErrors`, so unwrap it and hand over the auth codes it carried. A
// network failure, a parse failure and a refused MUTATION are all `is()`-false or code-free here and
// pass straight through to the caller that asked, untouched.
const denialLink = new ErrorLink(({ error }) => {
  if (!CombinedGraphQLErrors.is(error)) return;
  enterAfterDeniedRead(error.errors.map((graphQlError) => String(graphQlError.extensions?.code ?? '')));
});

export const apolloClient = new ApolloClient({
  link: from([denialLink, new HttpLink({ uri: '/graphql', credentials: 'include' })]),
  cache: new InMemoryCache(),
  // EVERY READ TRUSTS THE SERVER, because in a generated app nothing else can.
  //
  // Apollo's default is `cache-first`, and it is not merely a poor fit here — it cannot work. The
  // cache normalises on `id`/`_id` (InMemoryCache's defaultDataIdFromObject) and a generated schema
  // names its keys `filmId`, `bookId`, `habitId`: no generated type carries a bare `id`, so nothing
  // is ever a normalised entity. Every result lands as one opaque blob under `ROOT_QUERY.<field>`,
  // and the only thing that can change it is a full re-fetch of that same root field. Mutations
  // cannot help either — the schema returns `MutationResponse { success, error }` with nowhere to
  // put the record that was created, so there is no entity to write back and `refetchQueries` has
  // no ObservableQuery to find (a one-shot `client.query` never registers one).
  //
  // The rulebook already requires the behaviour this defends: "After a mutation settles, every read
  // it affects is re-invoked by the same machine … a stale list after a successful write is a
  // defect." A machine that obeys it to the letter still served a stale list, because the re-read
  // was answered from cache — a person catalogued a film, saw the list not move, and had no
  // in-app recovery at all (ws frhfds5bx; the app's own Refresh re-enters the same cached read).
  //
  // A per-call `fetchPolicy` still wins (mergeOptions compacts defaults under options), so a read
  // that genuinely wants the cache can still say so. `no-cache` was the alternative — leaner, but
  // it stops WRITING results too and forecloses any future cache-backed read for no gain today.
  defaultOptions: {
    query: { fetchPolicy: 'network-only' },
    watchQuery: { fetchPolicy: 'network-only' },
  },
});
